"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation as useI18nextTranslation } from "react-i18next";
import { $localizeEnabledFlag } from "@features/amplitude-flags/model";
import i18n, { APP_NS } from "i18n";

type TranslationPrimitive = string | number | boolean | Date | null | undefined;
type TranslationValue = TranslationPrimitive | { toString(): string };
type TranslationVariables = Record<string, TranslationValue>;

type UseTranslationOptions<T = string> = {
    variables?: TranslationVariables;
    fallback?: string;
    ignoreTranslate?: boolean;
    transform?: (value: string) => T;
};

const LOCALIZE_EVENTS = ["setLanguage", "sameLanguage", "updatedDictionary", "initialize"] as const;

// i18next must never split natural keys (English text = key) on "." or ":".
const NATURAL_KEY_OPTIONS = { keySeparator: false, nsSeparator: false, ns: APP_NS } as const;

const isLocalizeActive = (): boolean => $localizeEnabledFlag.getState() === true;

const formatVariableValue = (value: TranslationValue): string => {
    if (value === null || value === undefined) {
        return "";
    }
    if (value instanceof Date) {
        return value.toISOString();
    }
    if (typeof value === "number") {
        if (!Number.isFinite(value)) {
            return "";
        }
        return String(value);
    }
    if (typeof value === "boolean") {
        return value ? "true" : "false";
    }
    const stringified = String(value);
    if (stringified === "[object Object]") {
        try {
            return JSON.stringify(value);
        } catch {
            return "";
        }
    }
    return stringified;
};

const normalizeVariables = (variables?: TranslationVariables): Record<string, string> | undefined => {
    if (!variables) {
        return undefined;
    }
    const entries = Object.entries(variables).filter(([, value]) => value !== null && value !== undefined);
    if (!entries.length) {
        return undefined;
    }
    const normalized: Record<string, string> = {};
    for (const [key, value] of entries) {
        normalized[key] = formatVariableValue(value);
    }
    return normalized;
};

// Backend messages may use typographic quotes (’ “ ”) while dictionary keys
// use straight ones (or vice versa) — retry the lookup with normalized quotes.
const normalizeQuotes = (phrase: string): string => phrase.replace(/[’‘]/g, "'").replace(/[“”]/g, '"');

const translateWithI18next = (
    phrase: string,
    defaultValue: string,
    variables?: Record<string, string>
): string => {
    let key = phrase;
    if (!i18n.exists(key, NATURAL_KEY_OPTIONS)) {
        const normalized = normalizeQuotes(phrase);
        if (normalized !== phrase && i18n.exists(normalized, NATURAL_KEY_OPTIONS)) {
            key = normalized;
        }
    }
    const result = i18n.t(key, {
        ...NATURAL_KEY_OPTIONS,
        defaultValue,
        ...(variables ?? {})
    });
    return typeof result === "string" ? result : defaultValue;
};

const getImmediateTranslation = (
    phrase: string,
    fallback: string,
    variables?: Record<string, string>
): string => {
    if (typeof window === "undefined") {
        return fallback;
    }
    const instance = window.Localize;
    if (!instance || typeof instance.translate !== "function") {
        return fallback;
    }
    try {
        if (variables) {
            const result = instance.translate(phrase, variables);
            if (typeof result === "string") {
                return result;
            }
        } else {
            const result = instance.translate(phrase);
            if (typeof result === "string") {
                return result;
            }
        }
    } catch {
        return fallback;
    }
    return fallback;
};

export const useTranslation = <T = string>(phrase: string, options?: UseTranslationOptions<T>): T => {
    const { variables, fallback, transform, ignoreTranslate } = options ?? {};
    const localizeEnabled = isLocalizeActive();

    // Subscribe to i18next language changes (re-render on changeLanguage).
    const { i18n: i18nInstance } = useI18nextTranslation(APP_NS);
    const activeLanguage = i18nInstance.language;

    const transformFn = useMemo<(value: string) => T>(() => {
        if (transform) {
            return transform;
        }
        return (value: string) => value as unknown as T;
    }, [transform]);

    const normalizedVariables = useMemo(() => normalizeVariables(variables), [variables]);
    const defaultValue = fallback ?? phrase;

    // --- i18next path (active when Localize is disabled) ---
    const i18nValue = useMemo(() => {
        if (ignoreTranslate) {
            return defaultValue;
        }
        return translateWithI18next(phrase, defaultValue, normalizedVariables);
        // activeLanguage in deps so it recomputes on language change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phrase, defaultValue, ignoreTranslate, normalizedVariables, activeLanguage]);

    // --- Localize path (fallback, active only when $localizeEnabled === true) ---
    const [localizeValue, setLocalizeValue] = useState<string>(() =>
        localizeEnabled && !ignoreTranslate
            ? getImmediateTranslation(phrase, defaultValue, normalizedVariables)
            : defaultValue
    );
    const instanceRef = useRef<LocalizeInstance | null>(typeof window === "undefined" ? null : window.Localize ?? null);

    useEffect(() => {
        if (!localizeEnabled) {
            return;
        }
        if (ignoreTranslate) {
            setLocalizeValue(defaultValue);
            return;
        }
        setLocalizeValue((prev) => (prev === defaultValue ? prev : defaultValue));
        if (typeof window === "undefined") {
            return;
        }

        let active = true;
        let intervalId: number | null = null;
        let attachedInstance: LocalizeInstance | null = null;

        const apply = (value?: string) => {
            if (!active) {
                return;
            }
            const next = typeof value === "string" ? value : defaultValue;
            setLocalizeValue((prev) => (prev === next ? prev : next));
        };

        const translateWithInstance = (instance: LocalizeInstance) => {
            if (!active) {
                return;
            }
            instanceRef.current = instance;
            try {
                if (normalizedVariables) {
                    const immediate = instance.translate?.(phrase, normalizedVariables, apply);
                    if (typeof immediate === "string") {
                        apply(immediate);
                    }
                } else {
                    const immediate = instance.translate?.(phrase, apply);
                    if (typeof immediate === "string") {
                        apply(immediate);
                    }
                }
            } catch {
                apply(undefined);
            }
        };

        const handleEvent = () => {
            if (!instanceRef.current) {
                return;
            }
            translateWithInstance(instanceRef.current);
        };

        const attach = (instance: LocalizeInstance) => {
            attachedInstance = instance;
            translateWithInstance(instance);
            if (typeof instance.on === "function") {
                for (const eventName of LOCALIZE_EVENTS) {
                    instance.on(eventName, handleEvent);
                }
            }
        };

        const detach = () => {
            const activeInstance = attachedInstance ?? instanceRef.current;
            if (activeInstance && typeof activeInstance.off === "function") {
                for (const eventName of LOCALIZE_EVENTS) {
                    activeInstance.off(eventName, handleEvent);
                }
            }
        };

        const ensureInstance = () => {
            const instance = window.Localize;
            if (!instance || typeof instance.translate !== "function") {
                return false;
            }
            attach(instance);
            return true;
        };

        if (!ensureInstance()) {
            intervalId = window.setInterval(() => {
                if (ensureInstance() && intervalId !== null) {
                    window.clearInterval(intervalId);
                    intervalId = null;
                }
            }, 200);
        }

        return () => {
            active = false;
            if (intervalId !== null) {
                window.clearInterval(intervalId);
            }
            detach();
        };
    }, [localizeEnabled, phrase, ignoreTranslate, normalizedVariables, defaultValue]);

    const rawValue = localizeEnabled ? localizeValue : i18nValue;
    const translation = useMemo(() => transformFn(rawValue), [rawValue, transformFn]);
    return translation;
};

type TFunctionOptions = {
    fallback?: string;
    variables?: TranslationVariables;
};

/**
 * Ergonomic helper for components with many strings: returns a t(phrase, opts)
 * function so you don't call useTranslation per string.
 * When Localize is disabled (default) it uses i18next; when enabled it does a
 * synchronous best-effort Localize lookup (the DOM is translated by Localize anyway).
 */
export const useT = () => {
    const { i18n: i18nInstance } = useI18nextTranslation(APP_NS);
    const activeLanguage = i18nInstance.language;
    const localizeEnabled = isLocalizeActive();

    return useCallback(
        (phrase: string, opts?: TFunctionOptions): string => {
            const defaultValue = opts?.fallback ?? phrase;
            const variables = normalizeVariables(opts?.variables);
            if (localizeEnabled) {
                return getImmediateTranslation(phrase, defaultValue, variables);
            }
            return translateWithI18next(phrase, defaultValue, variables);
        },
        // activeLanguage so the function identity changes on language change
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [localizeEnabled, activeLanguage]
    );
};
